// A version of a file, or one file of a folder, shown as what the relay
// found its bytes to be (RFC-054 §3): text with its line numbers, Markdown
// rendered by markdown.tsx — React elements, never HTML — an image, a PDF in
// the browser's own viewer; anything else, and text too large to read here,
// only to download. HTML and SVG are text: their source, never rendered.
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { FilePreview as Kind } from "../api";
import { Markdown } from "../markdown";
import { t } from "../i18n";

/** Text past this is not shown: a page is no place to read 50 MB. */
export const maxShownText = 1 << 20;

/**
 * Whether an image's or a PDF's bytes are fetched and shown as a blob URL,
 * as text's are, rather than loaded by the browser from their URL. The demo
 * says so (src/demo/boot, byj-1wv.84.3): its fetch is the one it answers in
 * the page, where an <img> or <iframe> would ask the relay that served it.
 */
export const MediaByFetch = createContext(false);

const isMarkdown = (name: string) => /\.(md|markdown)$/i.test(name);

/** What a URL's bytes are, by the type the relay served them as. */
export function kindOf(contentType: string): Kind {
  if (contentType.startsWith("text/plain")) return "text";
  if (contentType.startsWith("image/")) return "image";
  if (contentType === "application/pdf") return "pdf";
  return "";
}

type Shown =
  | { state: "loading" }
  | { state: "error"; error: string }
  | { state: "text"; text: string }
  | { state: "url"; kind: "image" | "pdf"; url: string }
  | { state: "none" }
  | { state: "big" };

/**
 * Comments on lines (RFC-054 §7): how many threads each line has, the line
 * being commented on, and what pressing a line's mark does.
 */
export type LineComments = { marks: Map<number, number>; selected?: number; onLine: (line: number) => void };

/** Its text, with each line numbered; with comments, each line's mark to comment on it. */
function Lines({ text, comments }: { text: string; comments?: LineComments }) {
  const lines = text.endsWith("\n") ? text.slice(0, -1).split("\n") : text.split("\n");
  return (
    <ol className={`file-lines${comments ? " commentable" : ""}`} data-testid="file-lines">
      {lines.map((l, i) => {
        const n = i + 1;
        const k = comments?.marks.get(n) ?? 0;
        return (
          <li key={i} className={`${k > 0 ? "has-comments" : ""}${comments?.selected === n ? " selected" : ""}`}>
            {comments && (
              <button
                className={`line-mark${k > 0 ? " on" : ""}`}
                aria-label={k > 0 ? t("files:lineComments", { n, count: k }) : t("files:fileCommentOnLine", { n })}
                onClick={() => comments.onLine(n)}
              >
                {k > 0 ? k : "+"}
              </button>
            )}
            <code>{l || " "}</code>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * `url`'s bytes, named `name`: `kind` when the relay said what they are,
 * else (an entry of a folder) by the type it serves them as. `head` — a
 * folder's file's path and size — leads the line Markdown's Source stands at
 * the end of, `actions` (its Download) just before Source, so the two are one
 * bar over what is shown.
 */
export function FilePreview({ url, name, kind, bytes, comments, head, actions }: {
  url: string;
  name: string;
  kind?: Kind;
  bytes?: number;
  comments?: LineComments;
  head?: ReactNode;
  actions?: ReactNode;
}) {
  const [shown, setShown] = useState<Shown>({ state: "loading" });
  const [source, setSource] = useState(false);
  const byFetch = useContext(MediaByFetch);
  useEffect(() => {
    let live = true;
    let made: string | null = null;
    setShown({ state: "loading" });
    if ((kind === "image" || kind === "pdf") && !byFetch) {
      setShown({ state: "url", kind, url });
      return;
    }
    if (kind === "" || kind === "folder") {
      setShown({ state: "none" });
      return;
    }
    if (kind === "text" && bytes !== undefined && bytes > maxShownText) {
      setShown({ state: "big" });
      return;
    }
    (async () => {
      const res = await fetch(url, { credentials: "same-origin" });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const k = kind ?? kindOf(res.headers.get("Content-Type") ?? "");
      if (k === "text") {
        const text = await res.text();
        if (live) setShown(text.length > maxShownText ? { state: "big" } : { state: "text", text });
      } else if (k === "image" || k === "pdf") {
        made = URL.createObjectURL(await res.blob());
        if (live) setShown({ state: "url", kind: k, url: made });
      } else if (live) {
        setShown({ state: "none" });
      }
    })().catch((err: unknown) => live && setShown({ state: "error", error: err instanceof Error ? err.message : String(err) }));
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [url, kind, bytes, byFetch]);

  const bar = (toggle?: ReactNode) =>
    head || actions || toggle ? (
      <div className="row wrap file-bar">
        {head}
        <span className="grow" />
        {actions}
        {toggle}
      </div>
    ) : null;
  const shownAs = (body: ReactNode) =>
    head || actions ? (
      <div className="stack tight">
        {bar()}
        {body}
      </div>
    ) : (
      body
    );

  switch (shown.state) {
    case "loading":
      return shownAs(<p className="muted">{t("common:loading")}</p>);
    case "error":
      return shownAs(<p className="error">{t("common:error", { m: shown.error })}</p>);
    case "big":
      return shownAs(<p className="muted" data-testid="file-preview-big">{t("files:filePreviewTooBig")}</p>);
    case "none":
      return shownAs(<p className="muted" data-testid="file-preview-none">{t("files:filePreviewNone")}</p>);
    case "url":
      return shownAs(
        shown.kind === "image" ? (
          <img className="file-image" src={shown.url} alt={name} data-testid="file-image" />
        ) : (
          <iframe className="file-pdf" src={shown.url} title={name} data-testid="file-pdf" />
        ),
      );
    case "text":
      if (!isMarkdown(name)) return shownAs(<Lines text={shown.text} comments={comments} />);
      return (
        <div className="stack tight">
          {bar(
            <button className="small" aria-pressed={source} onClick={() => setSource((x) => !x)}>
              {source ? t("files:fileRendered") : t("files:fileSource")}
            </button>,
          )}
          {source ? (
            <Lines text={shown.text} comments={comments} />
          ) : (
            <>
              <div className="file-markdown" data-testid="file-markdown">
                <Markdown text={shown.text} />
              </div>
              {/* comments are on lines, which rendered Markdown has none of: one press to them */}
              {comments && (
                <p className="small flush file-comment-hint">
                  <span className="muted">{t("files:fileCommentsInSource")}</span>{" "}
                  <button className="small" onClick={() => setSource(true)}>
                    {t("files:fileCommentShowSource")}
                  </button>
                </p>
              )}
            </>
          )}
        </div>
      );
  }
}
